import { App, Flex, Form } from "antd";
import { type FC, useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
    getContragentsInfo,
    postContragentsCreateIllegal,
} from "tm-shared/api";
import { validationMessage } from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFTextField } from "@/shared/forms/RHF/RHFTextField";
import BaseButton from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText } from "@/shared/ui/Typography";
import {
    setPointCreatePageModal,
    setPointCreatePageModalSelectionPayload,
} from "@/store/slices/ui";
import { NewIllegalContragentFormValues } from "@/types/form";
import { PointCreateModal } from "@/types/modals";

interface AddIllegalEntityModalProps {
    onClose: () => void;
}

export const AddIllegalEntityModal: FC<AddIllegalEntityModalProps> = ({
    onClose,
}) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const { newPointFormValues } = useAppSelector((s) => s.ui.pointCreatePage);

    const emptyForm: NewIllegalContragentFormValues = useMemo(
        () => ({
            fullName: "",
            companyName: "",
        }),
        [],
    );

    const addIllegalEntityForm = useForm<NewIllegalContragentFormValues>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: emptyForm,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = addIllegalEntityForm;

    const onSubmit = useCallback(
        async ({ fullName, companyName }: NewIllegalContragentFormValues) => {
            const region = newPointFormValues?.region;

            try {
                if (region) {
                    const response = await postContragentsCreateIllegal({
                        fullName,
                        companyName,
                        mainRegionId: region,
                    });

                    const contragentId = response.result;

                    const { result: newContragent } = await getContragentsInfo({
                        contragent: contragentId,
                    });

                    dispatch(
                        setPointCreatePageModalSelectionPayload({
                            fieldName: "contragent",
                            option: {
                                label: newContragent.fullName,
                                value: newContragent.id,
                            },
                            value: newContragent.id,
                        }),
                    );

                    onClose();
                }
            } catch (error) {
                notification.error({
                    title: "Не вдалося створити тіньового контрагента",
                });

                return;
            }
        },
        [notification, dispatch, newPointFormValues?.region, onClose],
    );

    const handleCreateIllegal = useCallback(() => {
        dispatch(
            setPointCreatePageModal({
                type: PointCreateModal.AddIllegalContragent,
            }),
        );
    }, [dispatch]);

    return (
        <BaseModal
            open
            onClose={() => !isSubmitting && onClose()}
            actions={null}
            title={"Додати тіньового контрагента"}
            mask={{ closable: false }}
        >
            <Flex vertical gap={8}>
                <PrimaryText color="textDisabled">
                    Для додавання тіньового контрагента, заповніть форму
                </PrimaryText>

                <FormProvider {...addIllegalEntityForm}>
                    <Form
                        layout="vertical"
                        component="form"
                        onFinish={handleSubmit(onSubmit)}
                    >
                        <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                            <RHFTextField
                                name={"companyName"}
                                label={"Назва компанії"}
                                rules={{
                                    required: validationMessage.required,
                                }}
                                disabled={isSubmitting}
                                size="large"
                            />

                            <RHFTextField
                                name={"fullName"}
                                label={"Юридична особа"}
                                rules={{
                                    required: validationMessage.required,
                                }}
                                disabled={isSubmitting}
                                size="large"
                            />
                        </Flex>

                        <Flex vertical gap={8}>
                            <BaseButton
                                size="large"
                                type="default"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Скасувати
                            </BaseButton>

                            <BaseButton
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                                onClick={handleCreateIllegal}
                            >
                                Створити тіньового контрагента
                            </BaseButton>
                        </Flex>
                    </Form>
                </FormProvider>
            </Flex>
        </BaseModal>
    );
};
